import React, { useEffect, useMemo, useState } from 'react';
import {
  defaultSchoolGradeNumber,
  formatTrackLearningSourceLabel,
  formatTrackLearningSourceLines,
  listAdminLearningPublishersForTrack,
  listAdminLearningTemplateNamesForGrade,
  normalizeSchoolGradeNumbers,
  parseTrackLearningSourceSelection,
  serializeTrackLearningSourceSelection,
  type SchoolGradeNumber,
} from '../../utils/programLearningBatchGenerate';

type TrackLearningPublisherSelectProps = {
  workspaceId: string;
  classTitle: string;
  gradeNumbers: readonly number[];
  trackName: string;
  value: string;
  onChange: (publisher: string) => void;
  /** 편제(템플릿)를 고를 때. 확인 전에 호출된다. */
  onTemplateSelect?: (templateName: string) => void;
};

const TrackLearningPublisherSelect: React.FC<TrackLearningPublisherSelectProps> = ({
  workspaceId,
  classTitle,
  gradeNumbers,
  trackName,
  value,
  onChange,
  onTemplateSelect,
}) => {
  const gradeOptions = useMemo(() => normalizeSchoolGradeNumbers(gradeNumbers), [gradeNumbers]);
  const saved = useMemo(
    () => parseTrackLearningSourceSelection(value, trackName),
    [trackName, value]
  );
  const triggerLabel = formatTrackLearningSourceLabel(value, trackName);
  const triggerLines = formatTrackLearningSourceLines(value, trackName);
  const [open, setOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [publishersLoading, setPublishersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<SchoolGradeNumber>(() =>
    defaultSchoolGradeNumber(gradeOptions, saved.gradeNumber)
  );
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [draftPublisher, setDraftPublisher] = useState('');
  const [publishers, setPublishers] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !workspaceId) {
      setTemplatesLoading(false);
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    setError(null);
    setPublishers([]);
    void listAdminLearningTemplateNamesForGrade({ workspaceId, gradeNumber: selectedGrade })
      .then((names) => {
        if (cancelled) return;
        setTemplateNames(names);
        const savedTemplate = saved.templateName.trim();
        const exactTrack = trackName.trim();
        const savedMatchesGrade =
          !saved.gradeNumber || saved.gradeNumber === selectedGrade;
        if (savedMatchesGrade && savedTemplate && names.includes(savedTemplate)) {
          setSelectedTemplate(savedTemplate);
          return;
        }
        if (exactTrack && names.includes(exactTrack)) {
          setSelectedTemplate(exactTrack);
          return;
        }
        setSelectedTemplate('');
      })
      .catch((e) => {
        if (cancelled) return;
        setTemplateNames([]);
        setSelectedTemplate('');
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, saved.gradeNumber, saved.templateName, selectedGrade, trackName, workspaceId]);

  useEffect(() => {
    if (!open || !workspaceId || !selectedTemplate.trim()) {
      setPublishers([]);
      setPublishersLoading(false);
      return;
    }
    let cancelled = false;
    setPublishersLoading(true);
    void listAdminLearningPublishersForTrack({
      workspaceId,
      gradeNumber: selectedGrade,
      trackName: selectedTemplate,
    })
      .then((list) => {
        if (cancelled) return;
        setPublishers(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setPublishers([]);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setPublishersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedGrade, selectedTemplate, workspaceId]);

  const close = () => setOpen(false);

  const openModal = () => {
    const nextGrade = defaultSchoolGradeNumber(gradeOptions, saved.gradeNumber);
    setSelectedGrade(nextGrade);
    setDraftPublisher(saved.publisher.trim());
    setOpen(true);
  };

  const handleSelectGrade = (gradeNumber: SchoolGradeNumber) => {
    if (gradeNumber === selectedGrade) return;
    setSelectedGrade(gradeNumber);
    const savedMatchesGrade = !saved.gradeNumber || saved.gradeNumber === gradeNumber;
    if (savedMatchesGrade) {
      setDraftPublisher(saved.publisher.trim());
      return;
    }
    setDraftPublisher('');
  };

  const handleSelectTemplate = (name: string) => {
    setSelectedTemplate(name);
    onTemplateSelect?.(name);
    const savedTemplate = (saved.templateName || trackName).trim();
    const savedMatchesGrade = !saved.gradeNumber || saved.gradeNumber === selectedGrade;
    if (savedMatchesGrade && name === savedTemplate) {
      setDraftPublisher(saved.publisher.trim());
      return;
    }
    setDraftPublisher('');
  };

  const handleConfirm = () => {
    const publisher = draftPublisher.trim();
    const templateName = selectedTemplate.trim();
    if (!publisher || !templateName) return;
    onChange(
      serializeTrackLearningSourceSelection({
        templateName,
        publisher,
        gradeNumber: selectedGrade,
      })
    );
    close();
  };

  const handleClear = () => {
    setDraftPublisher('');
    onChange('');
    close();
  };

  const canConfirm = Boolean(selectedTemplate.trim() && draftPublisher.trim());
  const canClear = Boolean(triggerLabel || draftPublisher.trim());

  return (
    <div
      className="track-col-criteria track-col-publisher"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="track-col-criteria-label">출판사</span>
      <button
        type="button"
        className={'track-col-publisher-trigger' + (triggerLines ? ' has-value' : '')}
        onClick={openModal}
        title="관리자 학습내용의 학년·편제명·출판사를 선택합니다"
      >
        {triggerLines ? (
          <span className="track-col-publisher-trigger-lines">
            {triggerLines.headline ? (
              <span className="track-col-publisher-trigger-headline">{triggerLines.headline}</span>
            ) : null}
            <span className="track-col-publisher-trigger-publisher">{triggerLines.publisher}</span>
          </span>
        ) : (
          '선택'
        )}
      </button>
      {open ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            className="cp-modal cp-track-publisher-select-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-track-publisher-select-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="cp-track-publisher-select-head">
              <h3 id="cp-track-publisher-select-title" className="cp-track-publisher-select-head-title">
                <span className="cp-track-publisher-select-head-class">
                  <span className="cp-grade-track-class-block">
                    <span className="cp-grade-track-class-block-label">{classTitle.trim() || '클래스'}</span>
                    {gradeOptions.map((gradeNumber) => (
                      <span key={gradeNumber} className="class-grade-chip-display">
                        {gradeNumber}
                      </span>
                    ))}
                  </span>
                  <span className="cp-track-publisher-select-head-class-word">클래스</span>
                </span>
                <span className="cp-track-publisher-select-head-track">
                  편제명: {trackName.trim() || '편제'}
                </span>
              </h3>
            </header>
            <div className="cp-modal-body cp-track-learning-pick">
              <section className="cp-track-learning-pick-section">
                <h4 className="cp-track-learning-pick-title">학년 선택</h4>
                <div className="cp-track-learning-pick-pills" role="list">
                  {gradeOptions.map((gradeNumber) => {
                    const isActive = gradeNumber === selectedGrade;
                    return (
                      <button
                        key={gradeNumber}
                        type="button"
                        role="listitem"
                        className={'cp-track-learning-pick-pill' + (isActive ? ' is-active' : '')}
                        aria-pressed={isActive}
                        onClick={() => handleSelectGrade(gradeNumber)}
                      >
                        <span className="cp-track-learning-pick-dot" aria-hidden />
                        <span>{gradeNumber}학년</span>
                      </button>
                    );
                  })}
                </div>
              </section>
              <hr className="cp-track-learning-pick-divider" />
              {templatesLoading ? <p className="cp-track-publisher-select-status">편제명을 불러오는 중...</p> : null}
              {error ? <p className="cp-track-publisher-select-error">{error}</p> : null}
              {!templatesLoading && !error && templateNames.length === 0 ? (
                <p className="cp-track-publisher-select-status">이 학년에 등록된 관리자 학습내용이 없습니다.</p>
              ) : null}
              {!templatesLoading && templateNames.length > 0 ? (
                <section className="cp-track-learning-pick-section">
                  <h4 className="cp-track-learning-pick-title">편제 선택</h4>
                  <div className="cp-track-learning-pick-pills" role="list">
                    {templateNames.map((name) => {
                      const isActive = name === selectedTemplate;
                      return (
                        <button
                          key={name}
                          type="button"
                          role="listitem"
                          className={'cp-track-learning-pick-pill' + (isActive ? ' is-active' : '')}
                          aria-pressed={isActive}
                          onClick={() => handleSelectTemplate(name)}
                        >
                          <span className="cp-track-learning-pick-dot" aria-hidden />
                          <span>{name}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              {selectedTemplate ? (
                <>
                  <hr className="cp-track-learning-pick-divider" />
                  <section className="cp-track-learning-pick-section">
                    <h4 className="cp-track-learning-pick-title">출판사 학습내용 선택</h4>
                    {publishersLoading ? (
                      <p className="cp-track-publisher-select-status">출판사를 불러오는 중...</p>
                    ) : null}
                    {!publishersLoading && publishers.length === 0 ? (
                      <p className="cp-track-publisher-select-status">이 편제에 등록된 출판사가 없습니다.</p>
                    ) : null}
                    {!publishersLoading && publishers.length > 0 ? (
                      <div className="cp-track-learning-pick-pills cp-track-learning-pick-pills--publishers" role="list">
                        {publishers.map((publisher) => {
                          const isActive = publisher === draftPublisher;
                          return (
                            <button
                              key={publisher}
                              type="button"
                              role="listitem"
                              className={
                                'cp-track-learning-pick-pill cp-track-learning-pick-pill--publisher' +
                                (isActive ? ' is-active' : '')
                              }
                              aria-pressed={isActive}
                              onClick={() => setDraftPublisher(publisher)}
                            >
                              <span className="cp-track-learning-pick-check" aria-hidden>
                                {isActive ? '✓' : ''}
                              </span>
                              <span>{publisher}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                </>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={close}>
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={handleClear}
                disabled={!canClear}
              >
                선택 해제
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TrackLearningPublisherSelect;
